import { apiSlice } from './apiSlice'

/**
 * R1 Submission Packets — flow `dual_claimant_packets`.
 * Mỗi đội nộp 2 packet (scenario_a + scenario_b) ở Vòng sơ loại.
 */
export type R1Side = 'scenario_a' | 'scenario_b'

export interface R1SubmissionPacket {
  id: string
  case_id: string
  team_id: string
  r1_side: R1Side
  round_id?: string | null
  memorial_id?: string | null
  evidence_ids: string[]
  memorial_locked: boolean
  evidence_locked: boolean
  submitted_at?: string | null
  status:
    | 'draft'
    | 'submitted'
    | 'ai_scored'
    | 'admin_reviewed'
    | 'judge_reviewed'
    | 'locked'
  created_at?: string | null
  updated_at?: string | null
}

export interface R1SubmissionTeamSummary {
  case_id: string
  team_id: string
  scenario_a?: R1SubmissionPacket | null
  scenario_b?: R1SubmissionPacket | null
  fully_submitted: boolean
}

export const r1SubmissionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getR1Packet: builder.query<
      R1SubmissionPacket,
      { case_id: string; team_id: string; r1_side: R1Side }
    >({
      query: (params) => ({ url: '/r1-submissions/one', params }),
      providesTags: (_r, _e, { case_id, team_id, r1_side }) => [
        { type: 'R1Packet' as const, id: `${case_id}:${team_id}:${r1_side}` },
      ],
    }),
    getR1TeamSummary: builder.query<
      R1SubmissionTeamSummary,
      { case_id: string; team_id: string }
    >({
      query: (params) => ({ url: '/r1-submissions/team-summary', params }),
      providesTags: (_r, _e, { case_id, team_id }) => [
        { type: 'R1Packet' as const, id: `${case_id}:${team_id}` },
      ],
    }),
    getR1CaseSummary: builder.query<R1SubmissionTeamSummary[], { case_id: string }>({
      query: ({ case_id }) => ({ url: '/r1-submissions/case-summary', params: { case_id } }),
      providesTags: (_r, _e, { case_id }) => [
        { type: 'R1Packet' as const, id: `case:${case_id}` },
      ],
    }),
    setR1PacketEvidenceIds: builder.mutation<
      R1SubmissionPacket,
      { case_id: string; team_id: string; r1_side: R1Side; evidence_ids: string[] }
    >({
      query: (body) => ({
        url: '/r1-submissions/evidence-ids',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { case_id, team_id }) => [
        { type: 'R1Packet' as const, id: `${case_id}:${team_id}` },
        { type: 'R1Packet' as const, id: `case:${case_id}` },
        'Evidence',
      ],
    }),
    setR1PacketMemorialId: builder.mutation<
      R1SubmissionPacket,
      { case_id: string; team_id: string; r1_side: R1Side; memorial_id?: string | null }
    >({
      query: (body) => ({
        url: '/r1-submissions/memorial-id',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { case_id, team_id }) => [
        { type: 'R1Packet' as const, id: `${case_id}:${team_id}` },
        { type: 'R1Packet' as const, id: `case:${case_id}` },
        'Memorial',
      ],
    }),
  }),
})

export const {
  useGetR1PacketQuery,
  useGetR1TeamSummaryQuery,
  useGetR1CaseSummaryQuery,
  useSetR1PacketEvidenceIdsMutation,
  useSetR1PacketMemorialIdMutation,
} = r1SubmissionsApi
